import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { SwaggerResponse } from '@/services/config';
import {
    useLicenseController_archiveContract,
    useLicenseController_createContract,
    useLicenseController_deleteContractLicense,
    useLicenseController_getClientContracts,
    useLicenseController_unarchiveContract,
    useLicenseController_updateContract,
} from '@/services/hooks';
import {
    ClientController_GetClientSummaries,
    LicenseController_addContractLicense,
    LicenseController_updateContractLicense,
} from '@/services/services';
import type { Contract, CreateNewContractInput } from '@/services/types';

import type { ApiError } from '../shared/errors';
import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';
import { type DomainQuery, useMappedQuery } from '../shared/query';
import { sendWireBody } from '../shared/wireRequest';

import { toList } from './mappers';
import type { CreateNewContractLicenseInput } from './types';

/**
 * A client's bioscope contracts with their licences and placements. `onError` runs once for each
 * error the query settles on, as react-query's own query callback did before v5 removed it.
 */
export function useGetClientContracts(
    clientId: number,
    options?: { onError?: (error: ApiError) => void }
): DomainQuery<Contract[]> {
    const query = useMappedQuery(useLicenseController_getClientContracts(clientId), toList);
    const onError = options?.onError;
    const onErrorRef = useRef(onError);
    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        if (query.error) onErrorRef.current?.(query.error);
    }, [query.error]);

    return query;
}

export interface ContractLicenseChange {
    /** The contract the new licence is added to. */
    id: number;
    license: CreateNewContractLicenseInput;
}

/**
 * A contract licence row and the contract it belongs to. The API refuses a row that is not on
 * that contract, and files the change under the contract in the audit log.
 */
export interface ContractLicenseRef {
    contractId: number;
    contractLicenseId: number;
}

export interface ContractLicenseUpdate extends ContractLicenseRef {
    license: CreateNewContractLicenseInput;
}

export interface ContractChange {
    contractId: number;
    contract: CreateNewContractInput;
}

function addContractLicense({
    id,
    license,
}: ContractLicenseChange): Promise<SwaggerResponse<unknown>> {
    return sendWireBody('putRequest', LicenseController_addContractLicense, { id }, license);
}

function updateContractLicense({
    license,
    ...ref
}: ContractLicenseUpdate): Promise<SwaggerResponse<unknown>> {
    return sendWireBody(
        'patchRequest',
        LicenseController_updateContractLicense,
        contractLicensePath(ref),
        license
    );
}

function sameChange<T>(change: T): T {
    return change;
}

function newContractToApi(contract: CreateNewContractInput) {
    return { requestBody: contract };
}

function contractToApi({ contractId, contract }: ContractChange) {
    return { id: contractId, requestBody: contract };
}

function contractIdToApi(contractId: number) {
    return { id: contractId };
}

function contractLicensePath({ contractId, contractLicenseId }: ContractLicenseRef) {
    return { contractId, licenseId: contractLicenseId };
}

const ignore = (callbacks?: DomainMutationCallbacks) => toMutationOptions(callbacks, ignoreBody);

/** License edits change the internal clients table's expiry and health columns. */
function withClientSummariesInvalidation(
    queryClient: ReturnType<typeof useQueryClient>,
    callbacks?: DomainMutationCallbacks
): DomainMutationCallbacks {
    const { onSettled, ...rest } = callbacks ?? {};
    return {
        ...rest,
        onSettled: () => {
            onSettled?.();
            void queryClient.invalidateQueries({
                queryKey: [ClientController_GetClientSummaries.key],
            });
        },
    };
}

/** Adds a licence to a contract; the licence body may clear `dayTo` with null. */
export function useAddContractLicense(
    callbacks?: DomainMutationCallbacks
): DomainMutation<ContractLicenseChange, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useMutation<SwaggerResponse<unknown>, ApiError, ContractLicenseChange>({
            mutationFn: addContractLicense,
            ...ignore(withClientSummariesInvalidation(queryClient, callbacks)),
        }),
        sameChange,
        ignoreBody
    );
}

/** Updates a contract licence row; a null `dayTo` clears its end date. */
export function useUpdateContractLicense(
    callbacks?: DomainMutationCallbacks
): DomainMutation<ContractLicenseUpdate, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useMutation<SwaggerResponse<unknown>, ApiError, ContractLicenseUpdate>({
            mutationFn: updateContractLicense,
            ...ignore(withClientSummariesInvalidation(queryClient, callbacks)),
        }),
        sameChange,
        ignoreBody
    );
}

export function useDeleteContractLicense(
    callbacks?: DomainMutationCallbacks
): DomainMutation<ContractLicenseRef, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useLicenseController_deleteContractLicense(
            ignore(withClientSummariesInvalidation(queryClient, callbacks))
        ),
        contractLicensePath,
        ignoreBody
    );
}

export function useCreateContract(
    callbacks?: DomainMutationCallbacks
): DomainMutation<CreateNewContractInput, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useLicenseController_createContract(
            ignore(withClientSummariesInvalidation(queryClient, callbacks))
        ),
        newContractToApi,
        ignoreBody
    );
}

export function useUpdateContract(
    callbacks?: DomainMutationCallbacks
): DomainMutation<ContractChange, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useLicenseController_updateContract(
            ignore(withClientSummariesInvalidation(queryClient, callbacks))
        ),
        contractToApi,
        ignoreBody
    );
}

export function useArchiveContract(
    callbacks?: DomainMutationCallbacks
): DomainMutation<number, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useLicenseController_archiveContract(
            ignore(withClientSummariesInvalidation(queryClient, callbacks))
        ),
        contractIdToApi,
        ignoreBody
    );
}

export function useUnarchiveContract(
    callbacks?: DomainMutationCallbacks
): DomainMutation<number, void> {
    const queryClient = useQueryClient();
    return useMappedMutation(
        useLicenseController_unarchiveContract(
            ignore(withClientSummariesInvalidation(queryClient, callbacks))
        ),
        contractIdToApi,
        ignoreBody
    );
}
